import type Swiper from 'swiper';
import { getBreakpointValue } from '@sts/utils/tokens';

type SwiperRuntime = {
    Swiper: typeof Swiper;
    Navigation: unknown;
    Pagination: unknown;
};

declare global {
    interface Window {
        StsSwiperRuntime?: SwiperRuntime;
    }
}

const DATA_KEY = 'swiperInitialized';
const CAROUSEL_REGISTRY = new WeakMap<HTMLElement, { destroy: () => void }>();
const LISTING_REGISTRY = new WeakMap<HTMLElement, { update: () => void }>();
const BREAKPOINT_LG = getBreakpointValue('lg');
const TIME_ZONE_CACHE_KEY = 'sts:user-time-zone';
const TIME_ZONE_FETCH_TIMEOUT = 1800;
const DATE_TIME_WITH_ZONE_PATTERN = /(?:Z|[+\-]\d{2}:?\d{2})$/i;
const NAIVE_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/;

const getSwiperRuntime = (): SwiperRuntime | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.StsSwiperRuntime || null;
};

type TimeZoneApiProvider = {
    name: string;
    url: string;
    parseTimeZone: (payload: unknown) => string | null;
    skipOnHttps?: boolean;
};

const TIME_ZONE_API_PROVIDERS: TimeZoneApiProvider[] = [
    {
        name: 'worldtimeapi',
        url: 'https://worldtimeapi.org/api/ip',
        parseTimeZone: (payload) =>
            payload && typeof payload === 'object' && 'timezone' in payload
                ? String((payload as { timezone?: unknown }).timezone || '')
                : null,
    },
    {
        name: 'ipwhois',
        url: 'https://ipwho.is/',
        parseTimeZone: (payload) => {
            if (!payload || typeof payload !== 'object') {
                return null;
            }

            const success = 'success' in payload ? Boolean((payload as { success?: unknown }).success) : true;
            if (!success) {
                return null;
            }

            const timezoneValue = (payload as { timezone?: { id?: unknown } | unknown }).timezone;
            if (timezoneValue && typeof timezoneValue === 'object' && 'id' in timezoneValue) {
                return String((timezoneValue as { id?: unknown }).id || '');
            }

            return null;
        },
    },
    {
        name: 'ipapi',
        url: 'https://ipapi.co/json/',
        parseTimeZone: (payload) =>
            payload && typeof payload === 'object' && 'timezone' in payload
                ? String((payload as { timezone?: unknown }).timezone || '')
                : null,
    },
    {
        name: 'ip-api',
        url: 'http://ip-api.com/json/',
        skipOnHttps: true,
        parseTimeZone: (payload) => {
            if (!payload || typeof payload !== 'object') {
                return null;
            }

            const status = 'status' in payload ? String((payload as { status?: unknown }).status || '') : 'success';
            if (status !== 'success') {
                return null;
            }

            return 'timezone' in payload ? String((payload as { timezone?: unknown }).timezone || '') : null;
        },
    },
];

const sanitizeTimeZone = (value?: string | null): string | null => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (normalized === '') {
        return null;
    }

    try {
        new Intl.DateTimeFormat('en-US', { timeZone: normalized });
        return normalized;
    } catch {
        return null;
    }
};

const readCachedTimeZone = (contextWindow: Window): string | null => {
    try {
        const value = contextWindow.sessionStorage.getItem(TIME_ZONE_CACHE_KEY);
        return sanitizeTimeZone(value);
    } catch {
        return null;
    }
};

const writeCachedTimeZone = (contextWindow: Window, value: string) => {
    try {
        contextWindow.sessionStorage.setItem(TIME_ZONE_CACHE_KEY, value);
    } catch {
        // Ignore unavailable storage.
    }
};

const fetchJson = async (contextWindow: Window, url: string) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = contextWindow.setTimeout(() => {
        controller?.abort();
    }, TIME_ZONE_FETCH_TIMEOUT);

    try {
        const response = await contextWindow.fetch(url, {
            method: 'GET',
            cache: 'no-store',
            credentials: 'omit',
            signal: controller?.signal,
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            return null;
        }

        return (await response.json()) as unknown;
    } catch {
        return null;
    } finally {
        contextWindow.clearTimeout(timeoutId);
    }
};

const detectTimeZoneFromApis = async (contextWindow: Window): Promise<string | null> => {
    for (const provider of TIME_ZONE_API_PROVIDERS) {
        if (provider.skipOnHttps && contextWindow.location.protocol === 'https:') {
            continue;
        }

        const payload = await fetchJson(contextWindow, provider.url);
        if (!payload) {
            continue;
        }

        const resolved = sanitizeTimeZone(provider.parseTimeZone(payload));
        if (resolved) {
            return resolved;
        }
    }

    return null;
};

const detectTimeZoneFromIntl = (): string | null => {
    try {
        return sanitizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch {
        return null;
    }
};

let userTimeZonePromise: Promise<string | null> | null = null;

const resolveUserTimeZone = (contextWindow: Window): Promise<string | null> => {
    if (!userTimeZonePromise) {
        userTimeZonePromise = (async () => {
            const cached = readCachedTimeZone(contextWindow);
            if (cached) {
                return cached;
            }

            const fromApis = await detectTimeZoneFromApis(contextWindow);
            if (fromApis) {
                writeCachedTimeZone(contextWindow, fromApis);
                return fromApis;
            }

            const fromIntl = detectTimeZoneFromIntl();
            if (fromIntl) {
                writeCachedTimeZone(contextWindow, fromIntl);
            }

            return fromIntl;
        })()
            .then((resolved) => {
                if (!resolved) {
                    userTimeZonePromise = null;
                }
                return resolved;
            })
            .catch(() => {
                userTimeZonePromise = null;
                return null;
            });
    }

    return userTimeZonePromise;
};

const resolveLocale = (contextWindow: Window): string | undefined => {
    const documentLang = contextWindow.document?.documentElement?.lang?.trim();
    if (documentLang) {
        return documentLang;
    }

    if (contextWindow.navigator?.language) {
        return contextWindow.navigator.language;
    }

    return undefined;
};

type EventDateFormatterMap = {
    date: Intl.DateTimeFormat;
    time: Intl.DateTimeFormat;
};

const eventFormatterCache = new Map<string, EventDateFormatterMap>();
const timeZonePartFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getEventDateFormatters = (locale: string | undefined, timeZone: string): EventDateFormatterMap => {
    const cacheKey = `${locale || 'default'}::${timeZone}`;
    const cached = eventFormatterCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const formatters: EventDateFormatterMap = {
        date: new Intl.DateTimeFormat(locale, {
            timeZone,
            month: 'long',
            day: 'numeric',
        }),
        time: new Intl.DateTimeFormat(locale, {
            timeZone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }),
    };

    eventFormatterCache.set(cacheKey, formatters);

    return formatters;
};

const getTimeZonePartFormatter = (timeZone: string): Intl.DateTimeFormat => {
    const cached = timeZonePartFormatterCache.get(timeZone);
    if (cached) {
        return cached;
    }

    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    });

    timeZonePartFormatterCache.set(timeZone, formatter);

    return formatter;
};

const getTimeZoneOffsetMinutesAtInstant = (timeZone: string, date: Date): number | null => {
    try {
        const formatter = getTimeZonePartFormatter(timeZone);
        const parts = formatter.formatToParts(date);
        const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value || '';

        const year = Number(part('year'));
        const month = Number(part('month'));
        const day = Number(part('day'));
        const hour = Number(part('hour'));
        const minute = Number(part('minute'));
        const second = Number(part('second'));

        if ([year, month, day, hour, minute, second].some((value) => Number.isNaN(value))) {
            return null;
        }

        const asUtcTimestamp = Date.UTC(year, month - 1, day, hour, minute, second);

        return Math.round((asUtcTimestamp - date.getTime()) / 60000);
    } catch {
        return null;
    }
};

type ParsedNaiveDateTime = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

const parseNaiveDateTime = (value: string): ParsedNaiveDateTime | null => {
    const match = value.match(NAIVE_DATE_TIME_PATTERN);
    if (!match) {
        return null;
    }

    const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue] = match;

    const parsed: ParsedNaiveDateTime = {
        year: Number(yearValue),
        month: Number(monthValue),
        day: Number(dayValue),
        hour: Number(hourValue),
        minute: Number(minuteValue),
        second: Number(secondValue || '0'),
    };

    const values = [parsed.year, parsed.month, parsed.day, parsed.hour, parsed.minute, parsed.second];
    if (values.some((item) => Number.isNaN(item))) {
        return null;
    }

    return parsed;
};

const parseDateFromEventData = (
    eventUtcRaw: string,
    eventDateTimeRaw: string,
    eventTimeZoneRaw: string
): Date | null => {
    const eventUtc = eventUtcRaw.trim();
    if (eventUtc !== '') {
        const parsedUtc = new Date(eventUtc);
        if (!Number.isNaN(parsedUtc.getTime())) {
            return parsedUtc;
        }
    }

    const eventDateTime = eventDateTimeRaw.trim();
    if (eventDateTime === '') {
        return null;
    }

    if (DATE_TIME_WITH_ZONE_PATTERN.test(eventDateTime)) {
        const parsedWithZone = new Date(eventDateTime);
        if (!Number.isNaN(parsedWithZone.getTime())) {
            return parsedWithZone;
        }
    }

    const eventTimeZone = sanitizeTimeZone(eventTimeZoneRaw);
    if (!eventTimeZone) {
        const parsedWithoutZone = new Date(eventDateTime);
        return Number.isNaN(parsedWithoutZone.getTime()) ? null : parsedWithoutZone;
    }

    const parsedNaiveDateTime = parseNaiveDateTime(eventDateTime);
    if (!parsedNaiveDateTime) {
        const parsedWithoutZone = new Date(eventDateTime);
        return Number.isNaN(parsedWithoutZone.getTime()) ? null : parsedWithoutZone;
    }

    const utcGuess = Date.UTC(
        parsedNaiveDateTime.year,
        parsedNaiveDateTime.month - 1,
        parsedNaiveDateTime.day,
        parsedNaiveDateTime.hour,
        parsedNaiveDateTime.minute,
        parsedNaiveDateTime.second
    );

    const firstOffset = getTimeZoneOffsetMinutesAtInstant(eventTimeZone, new Date(utcGuess));
    if (firstOffset === null) {
        return null;
    }

    const firstPassUtc = utcGuess - firstOffset * 60000;
    const secondOffset = getTimeZoneOffsetMinutesAtInstant(eventTimeZone, new Date(firstPassUtc));
    if (secondOffset === null) {
        return new Date(firstPassUtc);
    }

    return new Date(utcGuess - secondOffset * 60000);
};

const localizeEventCardDateTimes = (root: ParentNode | Document, timeZone: string, contextWindow: Window) => {
    const locale = resolveLocale(contextWindow);
    const formatters = getEventDateFormatters(locale, timeZone);
    const cards = root.querySelectorAll<HTMLElement>('.sts-card-event[data-event-utc], .sts-card-event[data-event-date-time]');

    cards.forEach((card) => {
        const eventUtc = card.dataset.eventUtc || '';
        const eventDateTime = card.dataset.eventDateTime || '';
        const eventTimeZone = card.dataset.eventTimeZone || '';
        const parsedDate = parseDateFromEventData(eventUtc, eventDateTime, eventTimeZone);

        if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
            return;
        }

        const dateLabel = card.querySelector<HTMLElement>('[data-event-date]');
        if (dateLabel) {
            dateLabel.textContent = formatters.date.format(parsedDate);
        }

        const timeLabel = card.querySelector<HTMLElement>('[data-event-time]');
        if (timeLabel) {
            timeLabel.textContent = formatters.time.format(parsedDate);
        }
    });
};

const equalizeIconHeights = (container: HTMLElement) => {
    const icons = container.querySelectorAll<HTMLElement>('.sts-card__icons');
    if (!icons.length) {
        return;
    }

    let maxHeight = 0;
    icons.forEach((icon) => {
        icon.style.minHeight = '';
        const height = icon.offsetHeight;
        if (height > maxHeight) {
            maxHeight = height;
        }
    });

    if (maxHeight > 0) {
        icons.forEach((icon) => {
            icon.style.minHeight = `${maxHeight}px`;
        });
    }
};

const createUpdatePaginationInfo = (
    swiperInstance: Swiper,
    pagination: HTMLElement | null,
    container: HTMLElement
) => {
    return () => {
        const currentEl = pagination?.querySelector<HTMLElement>('.cards-controls__pagination-current');
        const totalEl = pagination?.querySelector<HTMLElement>('.cards-controls__pagination-total');
        if (!currentEl || !totalEl) {
            return;
        }
        const { currentScreen, totalScreens } = getSwiperScreenInfo(swiperInstance);

        currentEl.textContent = String(currentScreen);
        totalEl.textContent = String(totalScreens);

        equalizeIconHeights(container);
    };
};

const getSwiperScreenInfo = (swiperInstance: Swiper) => {
    const slides = Array.from(swiperInstance.slides) as HTMLElement[];
    const containerWidth = swiperInstance.el?.clientWidth || 0;
    if (!slides.length || containerWidth <= 0) {
        return { currentScreen: 1, totalScreens: 1 };
    }

    const getNextIndex = (startIndex: number) => {
        const startOffset = slides[startIndex]?.offsetLeft || 0;
        const maxRight = startOffset + containerWidth;
        let index = startIndex;
        for (; index < slides.length; index += 1) {
            const slide = slides[index];
            const right = slide.offsetLeft + slide.offsetWidth;
            if (right <= maxRight + 1) {
                continue;
            }
            break;
        }
        return index === startIndex ? startIndex + 1 : index;
    };

    let totalScreens = 0;
    for (let index = 0; index < slides.length; ) {
        totalScreens += 1;
        const nextIndex = getNextIndex(index);
        index = nextIndex > index ? nextIndex : index + 1;
    }

    let currentScreen = 1;
    const isLastScreen = swiperInstance.progress === 1;
    if (isLastScreen) {
        currentScreen = totalScreens;
    } else {
        for (let index = 0, screen = 1; index < slides.length; screen += 1) {
            const nextIndex = getNextIndex(index);
            if (swiperInstance.activeIndex < nextIndex) {
                currentScreen = screen;
                break;
            }
            index = nextIndex > index ? nextIndex : index + 1;
        }
    }

    return {
        currentScreen: Math.min(totalScreens, Math.max(1, currentScreen)),
        totalScreens: Math.max(1, totalScreens),
    };
};

const createSwiperConfig = (
    swiperRuntime: SwiperRuntime,
    prevButton: HTMLElement | null,
    nextButton: HTMLElement | null,
    pagination: HTMLElement | null
) => ({
    modules: [swiperRuntime.Navigation, swiperRuntime.Pagination],
    slidesPerView: 'auto' as const,
    spaceBetween: 16,
    slidesPerGroupAuto: true,
    watchSlidesProgress: true,
    navigation: {
        prevEl: prevButton || undefined,
        nextEl: nextButton || undefined,
        disabledClass: 'is-disabled',
    },
    pagination: {
        el: pagination || undefined,
        type: 'custom' as const,
        currentClass: 'cards-controls__pagination-current',
        totalClass: 'cards-controls__pagination-total',
        renderCustom: (swiperInstance: Swiper, _current: number, _total: number) => {
            const { currentScreen, totalScreens } = getSwiperScreenInfo(swiperInstance);
            return `<span class="cards-controls__pagination-current">${currentScreen}</span><span class="cards-controls__pagination-divider">/</span><span class="cards-controls__pagination-total">${totalScreens}</span>`;
        },
    },
    breakpoints: {
        [BREAKPOINT_LG]: {
            spaceBetween: 24,
        },
    },
});

const registerSwiperEvents = (swiperInstance: Swiper, updateCallback: () => void) => {
    swiperInstance.on('resize', updateCallback);
    swiperInstance.on('snapGridLengthChange', updateCallback);
    swiperInstance.on('slidesLengthChange', updateCallback);
    swiperInstance.on('setTranslate', updateCallback);
    swiperInstance.on('observerUpdate', updateCallback);
    swiperInstance.on('slideChange', updateCallback);
    swiperInstance.on('snapIndexChange', updateCallback);
    swiperInstance.on('transitionEnd', updateCallback);
};

const initPreviewCarousel = (block: HTMLElement, list: HTMLElement) => {
    const swiperRuntime = getSwiperRuntime();
    if (!swiperRuntime) {
        return;
    }

    const existing = CAROUSEL_REGISTRY.get(block);
    if (existing) {
        existing.destroy();
    }

    const cards = Array.from(list.children) as HTMLElement[];
    if (!cards.length) {
        return;
    }

    const doc = block.ownerDocument || document;
    const container = doc.createElement('div');
    container.classList.add(
        'sts-block-cards__preview-carousel',
        'sts-block-cards__list',
        'sts-block-cards__list--carousel',
        'swiper'
    );

    const wrapper = doc.createElement('div');
    wrapper.classList.add('swiper-wrapper');

    cards.forEach((card) => {
        const slide = doc.createElement('div');
        slide.classList.add('swiper-slide');
        slide.appendChild(card.cloneNode(true));
        wrapper.appendChild(slide);
    });

    container.appendChild(wrapper);
    list.insertAdjacentElement('afterend', container);

    const prevButton = block.querySelector<HTMLElement>('[data-swiper-nav="prev"]');
    const nextButton = block.querySelector<HTMLElement>('[data-swiper-nav="next"]');
    const pagination = block.querySelector<HTMLElement>('[data-swiper-pagination]');

    const previousDisplay = list.style.display;
    list.dataset.previewDisplay = previousDisplay;
    list.style.display = 'none';
    block.classList.add('has-swiper');

    const swiperInstance = new swiperRuntime.Swiper(
        container,
        createSwiperConfig(swiperRuntime, prevButton, nextButton, pagination)
    );
    const updatePaginationInfo = createUpdatePaginationInfo(swiperInstance, pagination, container);

    registerSwiperEvents(swiperInstance, updatePaginationInfo);
    updatePaginationInfo();

    const cleanup = () => {
        swiperInstance.destroy(true, true);
        container.remove();
        list.style.display = list.dataset.previewDisplay || '';
        delete list.dataset.previewDisplay;
        block.classList.remove('has-swiper');
    };

    CAROUSEL_REGISTRY.set(block, { destroy: cleanup });
};

const initCarousel = (block: HTMLElement) => {
    const swiperRuntime = getSwiperRuntime();
    if (!swiperRuntime) {
        return;
    }

    const list = block.querySelector<HTMLElement>('.sts-block-cards__list');
    if (!list) {
        return;
    }

    const isPreview = block.dataset.editorPreview === 'true';
    if (isPreview) {
        initPreviewCarousel(block, list);
        return;
    }

    const previewEntry = CAROUSEL_REGISTRY.get(block);
    if (previewEntry) {
        previewEntry.destroy();
        CAROUSEL_REGISTRY.delete(block);
    }

    if (list.dataset[DATA_KEY] === 'true') {
        return;
    }

    const items = Array.from(list.children) as HTMLElement[];
    if (items.length === 0) {
        return;
    }

    const wrapper = document.createElement('div');
    wrapper.classList.add('swiper-wrapper');

    items.forEach((item) => {
        item.classList.add('swiper-slide');
        wrapper.appendChild(item);
    });

    list.replaceChildren(wrapper);
    list.classList.add('swiper');
    block.classList.add('has-swiper');
    list.dataset[DATA_KEY] = 'true';

    const prevButton = block.querySelector<HTMLElement>('[data-swiper-nav="prev"]');
    const nextButton = block.querySelector<HTMLElement>('[data-swiper-nav="next"]');
    const pagination = block.querySelector<HTMLElement>('[data-swiper-pagination]');

    const swiperInstance = new swiperRuntime.Swiper(
        list,
        createSwiperConfig(swiperRuntime, prevButton, nextButton, pagination)
    );
    const updatePaginationInfo = createUpdatePaginationInfo(swiperInstance, pagination, block);

    registerSwiperEvents(swiperInstance, updatePaginationInfo);
    updatePaginationInfo();
};

export type ListingBreakpoint = { width: number; columns: number };
export type ListingColumnsMap = Record<'xlg' | 'lg', ListingBreakpoint[]>;

const LISTING_COLUMNS_BASE: ListingBreakpoint[] = [
    { width: BREAKPOINT_LG, columns: 4 },
    { width: 0, columns: 2 },
];

const LISTING_COLUMNS: ListingColumnsMap = {
    xlg: [...LISTING_COLUMNS_BASE],
    lg: [...LISTING_COLUMNS_BASE],
};

const getBlockMaxWidthSlug = (block?: HTMLElement | null): keyof ListingColumnsMap => {
    const list = block?.querySelector<HTMLElement>('.sts-block-cards__list');
    return list?.classList?.contains('has-max-width-lg') ? 'lg' : 'xlg';
};

const getListingLimit = (
    contextWindow?: Window | null,
    block?: HTMLElement | null,
    columns: ListingColumnsMap = LISTING_COLUMNS
) => {
    const maxWidthSlug = getBlockMaxWidthSlug(block);
    const columnConfig = columns[maxWidthSlug] || columns.xlg;
    const viewport =
        contextWindow?.innerWidth ||
        (typeof window !== 'undefined' ? window.innerWidth || 0 : 0);
    const fallback = columnConfig[columnConfig.length - 1] || { width: 0, columns: 2 };
    const config = columnConfig.find((entry) => viewport >= entry.width) || fallback;
    const fallbackColumns = config?.columns || fallback.columns;
    const usesBaseCardsLayout = block?.classList.contains('wp-block-sts-cards');
    const computedStyle =
        usesBaseCardsLayout && block && contextWindow?.getComputedStyle
            ? contextWindow.getComputedStyle(block)
            : null;
    const readPositiveInteger = (property: string, defaultValue: number) => {
        const value = Number.parseInt(computedStyle?.getPropertyValue(property).trim() || '', 10);
        return Number.isFinite(value) && value > 0 ? value : defaultValue;
    };
    const visibleColumns = readPositiveInteger(
        '--sts-cards-effective-listing-columns',
        fallbackColumns
    );
    const visibleRows = readPositiveInteger(
        '--sts-cards-effective-listing-rows',
        2
    );

    return visibleColumns * visibleRows;
};

const initListing = (block: HTMLElement, columns: ListingColumnsMap = LISTING_COLUMNS) => {
    const existing = LISTING_REGISTRY.get(block);
    if (existing) {
        existing.update();
        return;
    }

    const contextWindow = block.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : undefined);
    const pagination = block.querySelector<HTMLElement>('[data-listing-pagination]');
    const toggle = block.querySelector<HTMLButtonElement>('[data-listing-toggle]');

    if (!pagination || !toggle) {
        return;
    }

    const currentEl = pagination.querySelector<HTMLElement>('.cards-controls__pagination-current');
    const totalEl = pagination.querySelector<HTMLElement>('.cards-controls__pagination-total');

    if (!currentEl || !totalEl) {
        return;
    }

    const showLabel = toggle.dataset.showLabel || 'Show all';
    const hideLabel = toggle.dataset.hideLabel || 'Hide';
    let userExpanded = block.classList.contains('is-expanded');

    const update = () => {
        const limit = getListingLimit(contextWindow, block, columns);
        const cards = Array.from(block.querySelectorAll<HTMLElement>('.sts-card'));
        const total = cards.length;
        const shouldCollapse = total > limit;
        const effectiveExpanded = !shouldCollapse || userExpanded;

        block.classList.toggle('is-expanded', effectiveExpanded);
        cards.forEach((card, index) => {
            card.classList.toggle(
                'is-listing-hidden',
                !effectiveExpanded && index >= limit
            );
        });
        toggle.style.display = shouldCollapse ? '' : 'none';
        toggle.textContent = effectiveExpanded ? hideLabel : showLabel;
        toggle.setAttribute('aria-expanded', String(effectiveExpanded));

        const visible = effectiveExpanded ? total : Math.min(total, limit);
        currentEl.textContent = String(visible);
        totalEl.textContent = String(total);
    };

    const handleResize = () => {
        const raf =
            contextWindow?.requestAnimationFrame ||
            (typeof window !== 'undefined' ? window.requestAnimationFrame : null);
        if (typeof raf === 'function') {
            raf(update);
            return;
        }
        update();
    };

    toggle.addEventListener('click', () => {
        userExpanded = !userExpanded;
        update();
    });

    contextWindow?.addEventListener('resize', handleResize);

    const state = { update };
    LISTING_REGISTRY.set(block, state);
    update();
};

export const initCardsCarousel = (root: ParentNode | Document | null = document) => {
    if (!root) {
        return;
    }

    const blocks = root.querySelectorAll<HTMLElement>('.sts-block-cards--carousel');
    blocks.forEach((block) => initCarousel(block));
};

export const destroyCardsCarousel = (root: ParentNode | Document | null = document) => {
    if (!root) {
        return;
    }

    const blocks = root.querySelectorAll<HTMLElement>('.sts-block-cards--carousel[data-editor-preview="true"]');
    blocks.forEach((block) => {
        const entry = CAROUSEL_REGISTRY.get(block);
        if (entry) {
            entry.destroy();
            CAROUSEL_REGISTRY.delete(block);
        }

        const list = block.querySelector<HTMLElement>('.sts-block-cards__list');
        if (list) {
            list.style.display = list.dataset.previewDisplay || '';
            delete list.dataset.previewDisplay;
        }

        block.classList.remove('has-swiper');
    });
};

type InitCardsListingOptions = {
    blockSelector?: string;
    columns?: ListingColumnsMap;
};

export const initCardsListing = (
    root: ParentNode | Document | null = document,
    options?: InitCardsListingOptions
) => {
    if (!root) {
        return;
    }

    const blockSelector = options?.blockSelector || '.wp-block-sts-cards.sts-block-cards--listing';
    const columns = options?.columns || LISTING_COLUMNS;
    const blocks = root.querySelectorAll<HTMLElement>(blockSelector);
    blocks.forEach((block) => initListing(block, columns));
};

export const initCardsEventTimes = (root: ParentNode | Document | null = document) => {
    if (!root) {
        return;
    }

    const hasEventCards = root.querySelector('.sts-card-event[data-event-utc], .sts-card-event[data-event-date-time]');
    if (!hasEventCards) {
        return;
    }

    const contextWindow =
        root instanceof Document
            ? root.defaultView
            : root.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : null);
    if (!contextWindow) {
        return;
    }

    void resolveUserTimeZone(contextWindow).then((timeZone) => {
        if (!timeZone) {
            return;
        }

        localizeEventCardDateTimes(root, timeZone, contextWindow);
    });
};

const init = () => {
    initCardsCarousel(document);
    initCardsListing(document);
    initCardsEventTimes(document);
};

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}
